/**
 * Selection-aware telemetry registry (simulated, PoC).
 *
 * Every home-stage asset declares:
 *   bar     — the three metrics the app bar shows when nothing with its own
 *             telemetry is selected;
 *   groups  — telemetry for a part of the assembly, keyed by the CAD id of the
 *             assembly or sub-assembly it belongs to (public/cad/*_assembly.json).
 *
 * Resolution is by the numeric path embedded in CAD ids, so a unit such as
 * U6_3_3_R_BRAKE_ASSEMBLY resolves to the most specific declared group
 * (S6_3_R_MAIN_GEAR, else A6_LANDING_GEAR). Parts without a group show no
 * telemetry rather than borrowing another system's numbers.
 *
 * All values are illustrative demo data. Metric `get(ctx)` reads the live
 * simulated EV state, the fictional jet status or the fictional vacuum pump's
 * snapshot (data/industrial); `wobble` adds a small
 * periodic drift so readings look live without random jumps.
 */

import { aircraftStatus, aircraftFaults } from './aviation/dummyAircraftData.js';
import { vacuumPumpStatus, vacuumPumpFaults } from './industrial/dummyVacuumPumpData.js';

const rangeKm = (battery) => (typeof battery?.range === 'object' ? battery.range.estimated : battery?.range) ?? 0;

/**
 * Metric fields: key, label, unit, decimals, get(ctx) -> number|string,
 * optional wobble (amplitude in the metric's unit), optional thresholds:
 *   warnAbove / badAbove  or  warnBelow / badBelow.
 */
const m = (key, label, unit, get, opts = {}) => ({ key, label, unit, get, decimals: 0, ...opts });

const ev = {
  bar: [
    m('soc', 'SOC', '%', (c) => c.vehicleData.battery.soc, { decimals: 1, warnBelow: 20, badBelow: 10 }),
    m('range', 'Range', 'km', (c) => rangeKm(c.vehicleData.battery), { warnBelow: 60, badBelow: 25 }),
    m('packTemp', 'Pack temp', '°C', (c) => c.vehicleData.battery.temperature.pack, { decimals: 1, warnAbove: 40, badAbove: 50 }),
  ],
  location: (c) => c.vehicleData.vehicle?.location?.city,
  alerts: (c) => c.vehicleData.alerts?.length ?? 0,
  groups: {
    A3_ENERGY_STORAGE: {
      title: 'HV battery pack',
      metrics: [
        m('soc', 'State of charge', '%', (c) => c.vehicleData.battery.soc, { decimals: 1, warnBelow: 20, badBelow: 10 }),
        m('soh', 'State of health', '%', (c) => c.vehicleData.battery.soh, { warnBelow: 80, badBelow: 70 }),
        m('packTemp', 'Pack temp', '°C', (c) => c.vehicleData.battery.temperature.pack, { decimals: 1, warnAbove: 40, badAbove: 50 }),
        m('voltage', 'Pack voltage', 'V', (c) => c.vehicleData.battery.voltage, { decimals: 1 }),
      ],
    },
    A2_POWERTRAIN: {
      title: 'Powertrain',
      metrics: [
        m('motorTemp', 'Motor temp', '°C', (c) => c.vehicleData.motor.temperature, { decimals: 1, warnAbove: 90, badAbove: 120 }),
        m('efficiency', 'Motor efficiency', '%', (c) => c.vehicleData.motor.efficiency, { decimals: 1 }),
        m('rpm', 'Motor speed', 'rpm', (c) => c.vehicleData.motor.rpm ?? 0),
        m('gearOil', 'Gearbox oil temp', '°C', () => 41, { wobble: 0.6, decimals: 1, warnAbove: 95 }),
      ],
    },
    A4_HV_ELECTRICAL: {
      title: 'HV electrical',
      metrics: [
        m('inverterTemp', 'Inverter temp', '°C', () => 47, { wobble: 0.8, decimals: 1, warnAbove: 75, badAbove: 90 }),
        m('dcdc', 'DC-DC output', 'V', () => 13.8, { wobble: 0.05, decimals: 2, warnBelow: 13.0 }),
        m('obc', 'On-board charger', '', () => 'Idle'),
        m('isolation', 'Isolation resistance', 'MΩ', () => 4.2, { wobble: 0.1, decimals: 1, warnBelow: 1.0, badBelow: 0.5 }),
      ],
    },
    A5_CONTROL_ELECTRONICS: {
      title: 'Control electronics',
      metrics: [
        m('lv', '12 V battery', 'V', () => 12.6, { wobble: 0.04, decimals: 2, warnBelow: 12.0, badBelow: 11.5 }),
        m('can', 'CAN errors (1 h)', '', () => 3, { warnAbove: 10, badAbove: 50 }),
        m('signal', 'Telematics signal', 'dBm', () => -78, { wobble: 2, warnBelow: -100 }),
      ],
    },
    A6_THERMAL_MANAGEMENT: {
      title: 'Thermal management',
      metrics: [
        m('coolant', 'Coolant temp', '°C', () => 34, { wobble: 0.5, decimals: 1, warnAbove: 60, badAbove: 70 }),
        m('pump', 'Coolant pump', 'rpm', () => 2150, { wobble: 30 }),
        m('coolantLevel', 'Coolant level', '%', () => 92, { warnBelow: 70, badBelow: 50 }),
      ],
    },
    S7_1_FRONT_L_WHEEL: {
      title: 'Wheels and tyres',
      metrics: [
        m('fl', 'Front left', 'bar', () => 2.3, { wobble: 0.01, decimals: 2, warnBelow: 2.1, badBelow: 1.8 }),
        m('fr', 'Front right', 'bar', () => 2.3, { wobble: 0.01, decimals: 2, warnBelow: 2.1, badBelow: 1.8 }),
        m('rl', 'Rear left', 'bar', () => 2.08, { wobble: 0.01, decimals: 2, warnBelow: 2.1, badBelow: 1.8 }),
        m('rr', 'Rear right', 'bar', () => 2.2, { wobble: 0.01, decimals: 2, warnBelow: 2.1, badBelow: 1.8 }),
      ],
    },
    S7_2_FRONT_L_BRAKE: {
      title: 'Brakes',
      metrics: [
        m('padFront', 'Front pad wear left', '%', () => 38, { warnAbove: 70, badAbove: 85 }),
        m('padRear', 'Rear shoe wear', '%', () => 22, { warnAbove: 70, badAbove: 85 }),
        m('brakeFluid', 'Brake fluid', '%', () => 96, { warnBelow: 80 }),
      ],
    },
    A7_RUNNING_GEAR: {
      title: 'Running gear',
      metrics: [
        m('odo', 'Odometer', 'km', (c) => c.vehicleData.vehicle?.odometer ?? 0),
        m('steer', 'Steering angle', '°', () => 0, { wobble: 0.3, decimals: 1 }),
      ],
    },
  },
};

const sedan = {
  bar: [
    m('fuel', 'Fuel', '%', () => 64, { warnBelow: 15, badBelow: 7 }),
    m('coolant', 'Coolant', '°C', () => 88, { wobble: 0.7, decimals: 1, warnAbove: 105, badAbove: 115 }),
    m('lv', '12 V', 'V', () => 12.5, { wobble: 0.03, decimals: 2, warnBelow: 12.0, badBelow: 11.5 }),
  ],
  location: () => 'Pune',
  alerts: () => 0,
  groups: {
    A3_POWERTRAIN: {
      title: 'Engine',
      metrics: [
        m('rpm', 'Engine speed', 'rpm', () => 780, { wobble: 15 }),
        m('coolant', 'Coolant temp', '°C', () => 88, { wobble: 0.7, decimals: 1, warnAbove: 105, badAbove: 115 }),
        m('oil', 'Oil pressure', 'bar', () => 2.1, { wobble: 0.05, decimals: 1, warnBelow: 1.0, badBelow: 0.6 }),
        m('intake', 'Intake air temp', '°C', () => 36, { wobble: 0.4, decimals: 1 }),
      ],
    },
    S3_6_RADIATOR: {
      title: 'Cooling',
      metrics: [
        m('coolant', 'Coolant temp', '°C', () => 88, { wobble: 0.7, decimals: 1, warnAbove: 105, badAbove: 115 }),
        m('fan', 'Radiator fan', '', () => 'Off'),
      ],
    },
    A5_WHEELS_BRAKES: {
      title: 'Wheels and tyres',
      metrics: [
        m('fl', 'Front left', 'bar', () => 2.2, { wobble: 0.01, decimals: 2, warnBelow: 2.0, badBelow: 1.7 }),
        m('fr', 'Front right', 'bar', () => 2.2, { wobble: 0.01, decimals: 2, warnBelow: 2.0, badBelow: 1.7 }),
        m('rl', 'Rear left', 'bar', () => 2.1, { wobble: 0.01, decimals: 2, warnBelow: 2.0, badBelow: 1.7 }),
        m('rr', 'Rear right', 'bar', () => 2.1, { wobble: 0.01, decimals: 2, warnBelow: 2.0, badBelow: 1.7 }),
      ],
    },
    S5_1_BRAKE: {
      title: 'Brakes',
      metrics: [
        m('pad', 'Front pad wear', '%', () => 44, { warnAbove: 70, badAbove: 85 }),
        m('fluid', 'Brake fluid', '%', () => 93, { warnBelow: 80 }),
      ],
    },
    A4_SUSPENSION_CHASSIS: {
      title: 'Suspension and steering',
      metrics: [
        m('steer', 'Steering angle', '°', () => 0, { wobble: 0.3, decimals: 1 }),
        m('rideFront', 'Front ride height', 'mm', () => 142, { wobble: 0.5 }),
      ],
    },
    A7_ELECTRICAL_MISC: {
      title: 'Electrical',
      metrics: [
        m('lv', '12 V battery', 'V', () => 12.5, { wobble: 0.03, decimals: 2, warnBelow: 12.0, badBelow: 11.5 }),
        m('alt', 'Alternator output', 'A', () => 18, { wobble: 0.6, decimals: 1 }),
      ],
    },
  },
};

const a = aircraftStatus;
const jet = {
  bar: [
    m('fuel', 'Fuel', 'kg', () => a.fuel.totalKg, { warnBelow: 1500, badBelow: 800 }),
    m('egtR', 'EGT margin R', '°C', () => a.engines.right.egtMarginC, { warnBelow: 20, badBelow: 5 }),
    m('hydB', 'Hyd B', 'psi', () => a.hydraulics.systemB.pressurePsi, { wobble: 6, warnBelow: 2800, badBelow: 2400 }),
  ],
  location: () => a.location,
  alerts: () => aircraftFaults.filter((f) => f.status === 'active').length,
  groups: {
    A5_POWERPLANT: {
      title: 'Powerplant',
      metrics: [
        m('egtL', 'EGT margin L', '°C', () => a.engines.left.egtMarginC, { warnBelow: 20, badBelow: 5 }),
        m('egtR', 'EGT margin R', '°C', () => a.engines.right.egtMarginC, { warnBelow: 20, badBelow: 5 }),
        m('oilL', 'Oil qty L', '%', () => a.engines.left.oilQtyPct, { warnBelow: 85, badBelow: 70 }),
        m('oilR', 'Oil qty R', '%', () => a.engines.right.oilQtyPct, { warnBelow: 85, badBelow: 70 }),
      ],
    },
    S5_1_L_ENGINE: {
      title: 'Left engine',
      metrics: [
        m('egt', 'EGT last run', '°C', () => a.engines.left.egtLastRunC),
        m('margin', 'EGT margin', '°C', () => a.engines.left.egtMarginC, { warnBelow: 20, badBelow: 5 }),
        m('oil', 'Oil quantity', '%', () => a.engines.left.oilQtyPct, { warnBelow: 85, badBelow: 70 }),
        m('vib', 'Vibration', 'ips', () => a.engines.left.vibrationIps, { decimals: 2, warnAbove: 0.3, badAbove: 0.5 }),
      ],
    },
    S5_2_R_ENGINE: {
      title: 'Right engine',
      metrics: [
        m('egt', 'EGT last run', '°C', () => a.engines.right.egtLastRunC),
        m('margin', 'EGT margin', '°C', () => a.engines.right.egtMarginC, { warnBelow: 20, badBelow: 5 }),
        m('oil', 'Oil quantity', '%', () => a.engines.right.oilQtyPct, { warnBelow: 85, badBelow: 70 }),
        m('vib', 'Vibration', 'ips', () => a.engines.right.vibrationIps, { decimals: 2, warnAbove: 0.3, badAbove: 0.5 }),
      ],
    },
    A6_LANDING_GEAR: {
      title: 'Landing gear',
      metrics: [
        m('brakeL', 'Brake temp L', '°C', () => a.landingGear.left.brakeTempC, { wobble: 0.8, warnAbove: 100, badAbove: 130 }),
        m('brakeR', 'Brake temp R', '°C', () => a.landingGear.right.brakeTempC, { wobble: 0.8, warnAbove: 100, badAbove: 130 }),
        m('tyreNose', 'Nose tyre', 'psi', () => a.landingGear.nose.tyrePsi, { warnBelow: 175 }),
      ],
    },
    S6_1_NOSE_GEAR: {
      title: 'Nose gear',
      metrics: [
        m('tyre', 'Tyre pressure', 'psi', () => a.landingGear.nose.tyrePsi, { warnBelow: 175, badBelow: 160 }),
        m('brake', 'Wheel temp', '°C', () => a.landingGear.nose.brakeTempC, { wobble: 0.4, warnAbove: 100, badAbove: 130 }),
      ],
    },
    S6_2_L_MAIN_GEAR: {
      title: 'Left main gear',
      metrics: [
        m('brake', 'Brake temp', '°C', () => a.landingGear.left.brakeTempC, { wobble: 0.8, warnAbove: 100, badAbove: 130 }),
        m('tyre', 'Tyre pressure', 'psi', () => a.landingGear.left.tyrePsi, { warnBelow: 195, badBelow: 180 }),
      ],
    },
    S6_3_R_MAIN_GEAR: {
      title: 'Right main gear',
      metrics: [
        m('brake', 'Brake temp', '°C', () => a.landingGear.right.brakeTempC, { wobble: 0.8, warnAbove: 100, badAbove: 130 }),
        m('tyre', 'Tyre pressure', 'psi', () => a.landingGear.right.tyrePsi, { warnBelow: 195, badBelow: 180 }),
        m('hydB', 'Hyd B pressure', 'psi', () => a.hydraulics.systemB.pressurePsi, { wobble: 6, warnBelow: 2800, badBelow: 2400 }),
      ],
    },
    S8_1_FUEL_SYSTEM: {
      title: 'Fuel system',
      metrics: [
        m('total', 'Fuel on board', 'kg', () => a.fuel.totalKg, { warnBelow: 1500, badBelow: 800 }),
        m('capacity', 'Capacity', 'kg', () => a.fuel.capacityKg),
        m('imbalance', 'Imbalance', 'kg', () => a.fuel.imbalanceKg, { warnAbove: 120, badAbove: 250 }),
      ],
    },
    S8_2_HYDRAULICS: {
      title: 'Hydraulics',
      metrics: [
        m('hydA', 'System A', 'psi', () => a.hydraulics.systemA.pressurePsi, { wobble: 6, warnBelow: 2800, badBelow: 2400 }),
        m('hydB', 'System B', 'psi', () => a.hydraulics.systemB.pressurePsi, { wobble: 6, warnBelow: 2800, badBelow: 2400 }),
        m('fluidA', 'Fluid A', '%', () => a.hydraulics.systemA.fluidPct, { warnBelow: 85, badBelow: 70 }),
        m('fluidB', 'Fluid B', '%', () => a.hydraulics.systemB.fluidPct, { warnBelow: 85, badBelow: 70 }),
      ],
    },
    S8_3_ELECTRICAL: {
      title: 'Electrical',
      metrics: [
        m('bat', 'Battery', 'V', () => a.electrical.batteryV, { wobble: 0.03, decimals: 1, warnBelow: 24, badBelow: 22 }),
        m('gen', 'Generators', '', () => a.electrical.generators),
      ],
    },
    A7_COCKPIT: {
      title: 'Cockpit',
      metrics: [
        m('o2', 'Oxygen', '%', () => a.cockpit.oxygenPct, { warnBelow: 80, badBelow: 60 }),
        m('bit', 'Avionics BIT', '', () => a.cockpit.avionicsBit),
        m('pins', 'Seat pins', '', () => a.cockpit.seatPins),
        m('canopy', 'Canopy seal', '', () => a.cockpit.canopySeal),
      ],
    },
  },
};

const vp = vacuumPumpStatus;
// Inlet thresholds follow the set point, and the label names it.
const setPoint = vp.inlet.setPointMbarA;
const vInlet = m('inletP', `Inlet (set ${setPoint})`, 'mbar(a)', () => vp.inlet.pressureMbarA,
  { wobble: 0.6, warnAbove: 2 * setPoint, badAbove: 10 * setPoint });
const vElemOut = m('elemOut', 'Element out', '°C', () => vp.element.outletTempC,
  { wobble: 0.4, decimals: 1, warnBelow: 80, warnAbove: 105, badAbove: 110 });
const vElemOutLong = { ...vElemOut, label: 'Element outlet' };
// Controller calculation from the last pump-down over 100 -> 50 mbar(a) (no flow meter on the skid);
// about 630 m³/h on the demo process when healthy.
const vPumping = m('pumping', 'Pumping speed (pump-down)', 'm³/h', () => vp.inlet.pumpingSpeedM3h, { warnBelow: 450 });
const vVib = m('vib', 'Element vibration', 'mm/s', () => vp.element.vibrationMmS, { decimals: 1, warnAbove: 4.5, badAbove: 7.1 });
const vHours = m('hours', 'Running hours', 'h', () => vp.runningHours);
const vPower = m('power', 'Shaft power', 'kW', () => vp.drive.shaftPowerKw, { decimals: 1, wobble: 0.15 });
const vTypical = m('typical', 'Typical at set point', '', () => vp.drive.typicalAtSetPoint);
// The injected-oil probe (U6_3_2), not the sump: the fault text also quotes a 70 °C sump figure.
const vOilT = m('oilT', 'Injected oil', '°C', () => vp.oil.temperatureC, { warnBelow: 75, warnAbove: 100, badAbove: 105 });
const vLevel = m('level', 'Oil level', '%', () => vp.oil.levelPct, { warnBelow: 40, badBelow: 25 });
const vFilterDp = m('filterDp', 'Oil filter dP', 'mbar', () => vp.oil.filterDpMbar, { warnAbove: 250, badAbove: 400 });
const vSepDp = m('sepDp', 'Separator dP (last pump-down)', 'mbar', () => vp.separator.dpMbar, { warnAbove: 400, badAbove: 600 });
const vAir = m('air', 'Cooling air', '°C', () => vp.cooling.airTempC, { warnAbove: 40, badAbove: 46 });
const vFan = m('fan', 'Cooler fan', '', () => vp.cooling.fan);
const vHeatsink = m('heatsink', 'Heatsink', '°C', () => vp.electrical.heatsinkC, { warnAbove: 75, badAbove: 85 });
const vDcBus = m('dcBus', 'DC bus', 'V', () => vp.electrical.dcBusV, { wobble: 2, warnBelow: 510, warnAbove: 720 });
const vGateway = m('gateway', 'IoT gateway', '', () => vp.electrical.gateway);
const vService = m('service', 'Next service', '', () => `${vp.nextService.kit} in ${vp.nextService.dueInHours} h`);

const vacuum = {
  bar: [
    vInlet,
    m('speed', 'Speed', 'rpm', () => vp.drive.speedRpm, { wobble: 8 }),
    vElemOut,
    vService,
  ],
  // Key readings the voice assistant always gets, next to the selected part's group, so a question
  // about another sensor is answered from that sensor (Dashboard voiceTelemetry `asset_metrics`).
  voiceKey: [
    vInlet,
    m('speed', 'Speed', 'rpm', () => vp.drive.speedRpm, { wobble: 8 }),
    vOilT,
    vElemOut,
    vSepDp,
  ],
  location: () => vp.location,
  // The service notice is not an alarm.
  alerts: () => vacuumPumpFaults.filter((f) => f.status === 'active' && f.severity !== 'info').length,
  groups: {
    A1_VACUUM_ELEMENT: {
      title: 'Vacuum element',
      metrics: [
        vElemOutLong,
        vInlet,
        vPumping,
        // Shaft power over the last pump-down's pumping speed, not the current operating point.
        m('specEnergy', 'Specific energy (last pump-down)', 'Wh/m³', () => vp.specificEnergyWhM3, { decimals: 1, warnAbove: 30 }),
      ],
    },
    S1_2_ROTORS: {
      title: 'Rotors',
      metrics: [
        vPumping,
        m('speed', 'Rotor speed', 'rpm', () => vp.drive.speedRpm),
        m('blankOff', `Blank-off (${vp.element.blankOffHours.toLocaleString('en-IN')} h)`, 'mbar(a)',
          () => vp.element.blankOffMbarA, { decimals: 1, warnAbove: 1, badAbove: 2 }),
      ],
    },
    S1_3_ELEMENT_BEARINGS: {
      title: 'Element bearings',
      metrics: [vVib, vHours, vElemOutLong],
    },
    A2_DRIVE: {
      title: 'Drive',
      metrics: [
        m('speed', 'Motor speed', 'rpm', () => vp.drive.speedRpm),
        vPower,
        vTypical,
        m('current', 'Motor current', 'A', () => vp.drive.motorCurrentA, { wobble: 0.3 }),
        m('winding', 'Winding temp', '°C', () => vp.drive.windingTempC, { warnAbove: 130, badAbove: 145 }),
      ],
    },
    S2_2_COUPLING: {
      title: 'Coupling',
      metrics: [m('speed', 'Speed', 'rpm', () => vp.drive.speedRpm), vPower, vVib],
    },
    A3_INLET_SYSTEM: {
      title: 'Inlet',
      metrics: [
        vInlet,
        m('setPoint', 'Set point', 'mbar(a)', () => setPoint),
        m('pumpDown', 'Pump-down to 50', 'min', () => vp.inlet.pumpDownMin, { warnAbove: 5 }),
        vPumping,
      ],
    },
    S3_2_NON_RETURN_VALVE: {
      title: 'Non-return valve',
      metrics: [
        m('rise', 'Spool rise after stop (inlet closed)', 'mbar', () => vp.nonReturnValve.riseAfterStopMbar, { warnAbove: 50, badAbove: 200 }),
        m('lastStop', 'Last stop', 'h', () => vp.nonReturnValve.lastStopHours),
      ],
    },
    S3_3_GAS_BALLAST: {
      title: 'Gas ballast',
      metrics: [
        m('cmd', 'Command', '', () => vp.gasBallast.commanded),
        m('coil', 'Coil current', 'mA', () => vp.gasBallast.coilCurrentMa, { warnBelow: 200, badBelow: 50 }),
        m('state', 'Ballast', '', () => vp.gasBallast.state),
        vOilT,
      ],
    },
    A4_OIL_CIRCUIT: {
      title: 'Oil circuit',
      metrics: [
        vOilT,
        vLevel,
        // 35 %RS warning, 95 %RS high warning: the V40-01 fault they raise is a warning, not critical.
        m('sat', 'Oil saturation', '%RS', () => vp.oil.saturationPct, { warnAbove: 35 }),
        // Last lab sample: at 100 %RS the sensor cannot say how much free water there is.
        m('water', 'Water in oil (lab)', 'ppm', () => vp.oil.waterPpm, { warnAbove: 1000 }),
        vFilterDp,
      ],
    },
    S4_2_OIL_FILTER: {
      title: 'Oil filter',
      metrics: [
        { ...vFilterDp, label: 'Filter dP' },
        m('filterHours', 'Hours since change', 'h', () => vp.oil.filterHours, { warnAbove: 4000 }),
      ],
    },
    S4_3_THERMOSTATIC_VALVE: {
      title: 'Thermostatic valve',
      metrics: [vOilT, vElemOutLong, m('port', 'Cooler port (inferred)', '', () => vp.cooling.coolerPort)],
    },
    A5_EXHAUST: {
      title: 'Exhaust',
      metrics: [
        vSepDp,
        m('exhP', 'Exhaust pressure', 'mbar(a)', () => vp.separator.exhaustMbarA, { warnAbove: 1150, badAbove: 1250 }),
        m('mist', 'Oil mist (operator report)', '', () => vp.separator.oilMist.split(' (')[0]),
      ],
    },
    S5_1_OIL_SEPARATOR: {
      title: 'Oil separator',
      metrics: [
        vSepDp,
        m('elemHours', 'Element hours', 'h', () => vp.separator.elementHours, { warnAbove: 8000 }),
        vLevel,
      ],
    },
    A6_COOLING: {
      title: 'Cooling',
      metrics: [vOilT, vElemOutLong, vAir, vFan],
    },
    S6_2_COOLING_FAN: {
      title: 'Cooling fan',
      metrics: [vFan, m('fanA', 'Fan motor current', 'A', () => vp.cooling.fanCurrentA, { decimals: 1 })],
    },
    A7_ELECTRICAL_CONTROL: {
      title: 'Electrical and control',
      metrics: [vHeatsink, vDcBus, m('current', 'Motor current', 'A', () => vp.drive.motorCurrentA), vGateway],
    },
    S7_2_VSD_INVERTER: {
      title: 'VSD inverter',
      metrics: [vHeatsink, vDcBus, m('hz', 'Output frequency', 'Hz', () => vp.drive.outputHz), vTypical],
    },
    S7_4_CONTROLLER: {
      title: 'Controller',
      metrics: [
        vHours,
        vService,
        m('overdue', 'Overdue kits', '', () => vp.nextService.overdue.join(', ')),
        vGateway,
      ],
    },
    A8_FRAME_CANOPY: {
      title: 'Frame and canopy',
      metrics: [m('doors', 'Doors', '', () => vp.frame.doors), vAir, vVib],
    },
    S8_2_ANTI_VIBRATION_MOUNTS: {
      title: 'Anti-vibration mounts',
      metrics: [vVib, vHours],
    },
  },
};

export const TELEMETRY = { ace_ev: ev, yaris: sedan, jet, vacuum_pump: vacuum };

/** Numeric path of a CAD id: 'U6_3_3_R_BRAKE_ASSEMBLY' -> ['6','3','3']. */
export function idPath(id) {
  const match = /^[A-Z]+(\d+(?:_\d+)*)(?=_|$)/.exec(String(id || ''));
  return match ? match[1].split('_') : null;
}

/** Most specific telemetry group key for a selected CAD id, or null. */
export function resolveGroupKey(assetId, selectedId) {
  const def = TELEMETRY[assetId];
  if (!def || !selectedId) return null;
  if (def.groups[selectedId]) return selectedId;
  const path = idPath(selectedId);
  if (!path) return null;
  let best = null;
  let bestLen = 0;
  for (const key of Object.keys(def.groups)) {
    const kp = idPath(key);
    if (!kp || kp.length > path.length || kp.length <= bestLen) continue;
    if (kp.every((seg, i) => seg === path[i])) {
      best = key;
      bestLen = kp.length;
    }
  }
  return best;
}

function statusOf(metric, value) {
  if (typeof value !== 'number') return null;
  if (metric.badAbove !== undefined && value > metric.badAbove) return 'bad';
  if (metric.badBelow !== undefined && value < metric.badBelow) return 'bad';
  if (metric.warnAbove !== undefined && value > metric.warnAbove) return 'warn';
  if (metric.warnBelow !== undefined && value < metric.warnBelow) return 'warn';
  if (metric.warnAbove !== undefined || metric.warnBelow !== undefined) return 'good';
  return null;
}

/** Which limit a warn/bad reading crossed: 'low' (e.g. oil running cold) or 'high'. */
function limitSide(metric, value) {
  if (typeof value !== 'number') return null;
  const low = [metric.badBelow, metric.warnBelow].some((lim) => lim !== undefined && value < lim);
  const high = [metric.badAbove, metric.warnAbove].some((lim) => lim !== undefined && value > lim);
  if (low) return 'low';
  return high ? 'high' : null;
}

/** Evaluate one metric at a simulation tick. */
export function readMetric(metric, ctx, tick) {
  let value = metric.get(ctx);
  if (typeof value === 'number' && metric.wobble) {
    const seed = [...metric.key].reduce((s, ch) => s + ch.charCodeAt(0), 0);
    value += metric.wobble * Math.sin(tick * 0.7 + seed);
  }
  const text = typeof value === 'number'
    ? value.toLocaleString('en-IN', { minimumFractionDigits: metric.decimals, maximumFractionDigits: metric.decimals })
    : String(value ?? '—');
  const status = statusOf(metric, value);
  const dir = status === 'warn' || status === 'bad' ? limitSide(metric, value) : null;
  return { key: metric.key, label: metric.label, unit: metric.unit, value, text, status, ...(dir ? { dir } : {}) };
}
